import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { API_URL, getCustomers, getEntries, type Entry } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  computeTimecardPeriodStats,
  getDatesInPeriod,
  punchesByCalendarDay,
} from "@/lib/timecardStats";

type PayWeekItem = { pay_week: string; from_date: string; to_date: string; label: string };

export function useTimeCardViewModel() {
  const { user } = useAuth();
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);
  const {
    data: payWeeksData,
    isLoading: payWeeksLoading,
  } = useQuery({
    queryKey: ["pay-weeks", currentYear],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/pay-weeks?year=${currentYear}`, { credentials: "include" });
      if (!resp.ok) return { pay_weeks: [] as PayWeekItem[], current: null as PayWeekItem | null };
      return resp.json() as Promise<{ pay_weeks: PayWeekItem[]; current: PayWeekItem | null }>;
    },
    enabled: !!user,
  });

  const { data: customersData } = useQuery({
    queryKey: ["customers", ""],
    queryFn: () => getCustomers(""),
    enabled: !!user,
  });

  const [timecardPayWeek, setTimecardPayWeek] = React.useState<string>("");
  const [timecardEntries, setTimecardEntries] = React.useState<Entry[]>([]);
  const [timecardLoading, setTimecardLoading] = React.useState(false);
  const [payWeekDefaultApplied, setPayWeekDefaultApplied] = React.useState(false);

  React.useEffect(() => {
    if (payWeekDefaultApplied || !payWeeksData?.current) return;
    const c = payWeeksData.current;
    setTimecardPayWeek((prev) => prev || c.from_date);
    setPayWeekDefaultApplied(true);
  }, [payWeeksData, payWeekDefaultApplied]);

  const [expandedDays, setExpandedDays] = React.useState<Set<string>>(new Set());

  function toggleDay(day: string) {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  }

  // When employee picks a payweek, fetch the range and compute the timecard.
  // timecardPayWeek stores the period from_date (unique); pay_week numbers repeat (02..52).
  React.useEffect(() => {
    if (!timecardPayWeek || !user || !payWeeksData?.pay_weeks) return;
    const w = payWeeksData.pay_weeks.find((x: any) => x.from_date === timecardPayWeek);
    if (!w) return;
    (async () => {
      setTimecardLoading(true);
      try {
        const p = new URLSearchParams();
        p.set("user_id", String(user.id));
        p.set("from_date", `${w.from_date}T00:00:00`);
        p.set("to_date", `${w.to_date}T23:59:59`);
        p.set("limit", "500");
        const data = await getEntries(p);
        setTimecardEntries(data);
      } catch (e) {
        setTimecardEntries([]);
      } finally {
        setTimecardLoading(false);
      }
    })();
  }, [timecardPayWeek, user, payWeeksData]);

  /** FK → business customer_id for CA OT when entry.customer_code is omitted. */
  const timecardCustomerCodeById = React.useMemo(() => {
    const m = new Map<number, string>();
    for (const c of customersData ?? []) {
      if (c.id != null && c.customer_id) m.set(c.id, c.customer_id);
    }
    return m;
  }, [customersData]);

  // Shared integer-hundredths OT (lib/timecardStats) — matches Manager PPA + backend policy.
  const timecardStats = React.useMemo(
    () =>
      computeTimecardPeriodStats(timecardEntries, {
        customerCodeById: timecardCustomerCodeById,
      }),
    [timecardEntries, timecardCustomerCodeById],
  );

  const timecardPunchesByDay = React.useMemo(
    () => punchesByCalendarDay(timecardEntries),
    [timecardEntries],
  );

  const selectedTimecardWeek = React.useMemo(() => {
    if (!timecardPayWeek || !payWeeksData?.pay_weeks) return null;
    return (payWeeksData.pay_weeks as any[]).find((w: any) => w.from_date === timecardPayWeek) || null;
  }, [timecardPayWeek, payWeeksData]);


  return {
    payWeeksData,
    payWeeksLoading,
    timecardPayWeek,
    setTimecardPayWeek,
    timecardEntries,
    timecardLoading,
    expandedDays,
    toggleDay,
    timecardStats,
    timecardPunchesByDay,
    selectedTimecardWeek,
  };
}
